import React, { useEffect, useState } from "react"
import { KSpan, KTitleSpan, KDropdown, KSelectRange, KButton, KTooltip } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { getSelectedLanguage, lang } from "../../../../constants/languages"
import upcomingColumns from "./upcomingColumns"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { summaries } from "../../../../apis"
import {
  getCurrency,
  getMonthRange,
  mapListToCamelCase,
  mapToCamelCase,
  yyyymmToDate,
  getLastNMonthsYYYYMM
} from "../../../../utility"
import { SummaryUpcoming, SummaryTotals, UpcomingTotals } from "../../../../types"
import Loading from "../../../ui/Loading"
import PlatformStatusDrawer from "./PlatformStatusDrawer"

const Upcoming = ({
  isAdmin,
  platforms,
  selectedUser,
  selectedAccount
}: {
  isAdmin: boolean
  platforms: KSelectOption[]
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}) => {
  const [loading, setLoading] = useState(false)

  const [offset, setOffset] = useState(0)
  const [summaryList, setSummaryList] = useState<SummaryUpcoming[]>([])
  const [upcomingTotals, setUpcomingTotals] = useState<UpcomingTotals[]>([])
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [selectedPeriodList, setSelectedPeriodList] = useState<string[]>(getLastNMonthsYYYYMM(6))
  // paymentDate of the card whose platform status drawer is open, so only one can be open at a time
  const [openStatusCard, setOpenStatusCard] = useState<string | null>(null)

  const monthYear = (key: string | null | undefined) => {
    if (!key) return "N/A"
    // backend deal_date / contract_date are "YYYYMM"

    const language = getSelectedLanguage()

    const year = key.slice(0, 4)
    const month = parseInt(key.slice(4, 6), 10)
    if (!year || isNaN(month)) return "N/A"
    const d = new Date(parseInt(year, 10), month - 1, 1)
    return d.toLocaleDateString(language, { month: "short", year: "numeric" })
  }

  const getRoyaltiesUpcoming = (
    selectedPeriodList: string[],
    selectedPlatforms: KSelectOption[],
    upcomingTotals?: UpcomingTotals[]
  ) => {
    setLoading(true)
    const isAdminParam = isAdmin ? "&is_admin_page=1" : "&is_admin_page=0"
    const dspParam =
      selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.map((option) => option.value).join(",")}` : ""
    const periodParam =
      selectedPeriodList.length > 0
        ? `&period_start=${selectedPeriodList[0]}&period_end=${selectedPeriodList[selectedPeriodList.length - 1]}`
        : ""
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const selectedAccountParam = `&selected_account=${selectedAccount?.value2}`
    const totalsParam = upcomingTotals?.length ? "&include_totals=0" : ""

    summaries(
      `/royalties_upcoming/?limit=${limit}&offset=${offset}${isAdminParam}${dspParam}${periodParam}${userIdParam}${selectedAccountParam}${totalsParam}`,
      "get"
    )
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          return
        }

        if (!upcomingTotals?.length && resp.data.totals) {
          const upcomingTotals: UpcomingTotals[] = mapListToCamelCase(resp.data.totals)
          setUpcomingTotals(upcomingTotals)
        }

        const summaryList: SummaryUpcoming[] = mapListToCamelCase(resp.data.results)
        setSummaryList(summaryList)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (isAdmin && !selectedUser?.value) {
      return
    }
    setUpcomingTotals([])
    setSummaryList([])
    setOpenStatusCard(null)
    setSelectedPlatforms([])
    setSelectedPeriodList(getLastNMonthsYYYYMM(6))
    getRoyaltiesUpcoming(getLastNMonthsYYYYMM(6), [], undefined)
  }, [selectedUser, selectedAccount])

  const limit = 10

  const topRightElements = () => {
    return (
      <div className="flex gap-4">
        <div className="flex gap-6">
          {selectedPeriodList.length > 0 && (
            <div className="flex">
              <KSelectRange
                key={`filter-date-range`}
                value={[
                  yyyymmToDate(selectedPeriodList[0]),
                  yyyymmToDate(selectedPeriodList[selectedPeriodList.length - 1])
                ]}
                onChange={(event) => {
                  if (!event) {
                    setSelectedPeriodList(getLastNMonthsYYYYMM(6))
                    getRoyaltiesUpcoming(getLastNMonthsYYYYMM(6), selectedPlatforms, upcomingTotals)
                    return
                  }
                  //@ts-ignore
                  const monthRage = getMonthRange(event[0], event[1])
                  if (monthRage.join(" ") === selectedPeriodList.join(" ")) {
                    return
                  }
                  setSelectedPeriodList(monthRage)
                  getRoyaltiesUpcoming(monthRage, selectedPlatforms, upcomingTotals)
                }}
                height="36px"
                width="auto"
                backgroundColor="#F7F7F7"
                hoverBackgroundColor="#F3F3F3"
                boxShadow="none"
              />
              <div className="flex ml-1 mr-2 items-center gap-1">
                <KSpan text={`${selectedPeriodList[0]}`} />
                {selectedPeriodList.length > 1 && (
                  <KSpan text={`- ${selectedPeriodList[selectedPeriodList.length - 1]}`} />
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-start max-w-[260px] min-w-[160px]">
            <KDropdown
              isClearable
              options={platforms}
              isMulti
              onSelect={(elements) => {
                setSelectedPlatforms(elements as KSelectOption[])
              }}
              placeholder={lang.analytics.all_platforms_placeholder}
              showOnlyIconsInMulti
              allowContainerShrink
              selected={selectedPlatforms}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={170}
              enableIndicator={true}
              placeholderColor="#000"
              closeMenuOnSelect={false}
              onBlur={() => getRoyaltiesUpcoming(selectedPeriodList, selectedPlatforms, upcomingTotals)}
            />
          </div>
        </div>
      </div>
    )
  }

  const checkCurrentMonth = (paymentDate: string) => {
    const now = new Date()
    const currentYearMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`
    return paymentDate === currentYearMonth
  }

  const openStatusTotal = upcomingTotals.find((total) => total.paymentDate === openStatusCard)

  const renderWidget = () => {
    return (
      <div className="flex gap-4 pr-8">
        {upcomingTotals.map((upcomingTotal) => {
          let revenue = upcomingTotal?.estimatedRevenue || 0
          if (revenue < 0) {
            revenue = 0
          }
          const estimatedRevenue = revenue.toLocaleString("en-EN", {
            maximumFractionDigits: 2
          })

          const currency = getCurrency(upcomingTotal?.currency || "")
          return (
            <div
              className="p-4"
              style={{ border: "1px solid #11111120", borderRadius: 5 }}
              key={`upcoming_total_${upcomingTotal.paymentDate}`}
            >
              <div className="mb-4 mt-1">
                <KSpan fontSize={32} text={monthYear(upcomingTotal.paymentDate)} />
              </div>
              <div className="flex justify-between items-center" style={{ width: 240 }}>
                <KSpan
                  text={
                    checkCurrentMonth(upcomingTotal.paymentDate)
                      ? `${lang.royalties.upcoming.widget.ongoing_revenue}`
                      : `${lang.royalties.upcoming.widget.estimated_revenue}`
                  }
                />
                {upcomingTotal.paymentQuarter && <KSpan text={`(${upcomingTotal.paymentQuarter})`} fontWeight={500} />}
              </div>
              <div className="mt-2">
                <KSpan
                  fontSize={28}
                  lineHeight="32px"
                  fontWeight={500}
                  color="#111"
                  text={`${currency}${estimatedRevenue} `}
                />
              </div>
              <div className="mt-5">
                {/* Progress bar */}
                <div className="w-full h-3 rounded bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-300"
                    style={{ width: `${upcomingTotal?.progress || 0}%` }}
                  />
                </div>
              </div>
              <div className="mt-1 flex gap-2 items-center">
                <KSpan
                  text={`${upcomingTotal?.progress?.toFixed(1) || 0}% ${lang.royalties.upcoming.widget.complete}`}
                />
                {!isAdmin ? (
                  <div
                    className="flex cursor-pointer"
                    onMouseEnter={() => setOpenStatusCard(upcomingTotal.paymentDate)}
                  >
                    <img width="16" height="16" src="/navigation_icons/help.svg" alt="platform-status" />
                  </div>
                ) : (
                  <KTooltip
                    position="bottom"
                    align="right"
                    children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
                    width={"480px"}
                    content={
                      <div>
                        <div>
                          <KSpan
                            fontSize={16}
                            color="#111"
                            fontWeight={600}
                            text={lang.royalties.upcoming.widget.complete_platform_list}
                          />
                          {upcomingTotal?.platformCompleteList?.map((platform, index) => (
                            <div key={`platform_complete_${index}`}>
                              <KSpan text={platform} />
                            </div>
                          ))}
                        </div>
                        <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #3838387c" }}>
                          <KSpan
                            fontSize={16}
                            color="#111"
                            fontWeight={600}
                            text={lang.royalties.upcoming.widget.missing_platform_list}
                          />
                          {upcomingTotal?.platformMissingList?.map((platform, index) => (
                            <div key={`platform_missing_${index}`}>
                              <KSpan text={platform || "-"} />
                            </div>
                          ))}
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          )
        })}
        {openStatusTotal && (
          <PlatformStatusDrawer upcomingTotal={openStatusTotal} onClose={() => setOpenStatusCard(null)} />
        )}
      </div>
    )
  }

  return (
    <div className="flex-grow justify-center">
      {loading && <Loading />}
      <div className="w-[100%] flex pt-6 pl-3">{renderWidget()}</div>
      <div className="w-[100%] flex ">
        <div className="grow min-h-0">
          <TableNew
            className="pt-2.5 -ml-5"
            title={<KTitleSpan fontSize={20} lineHeight="26px" text={lang.royalties.upcoming.title} />}
            minRowCount={limit}
            limit={limit}
            columns={upcomingColumns}
            data={summaryList}
            count={1}
            loading={loading}
            topRightElement={topRightElements()}
            overrideTableOverflow="auto"
            notFoundTitle={lang.not_found.reports.title}
            notFoundDesc={lang.not_found.reports.desc}
            tablePaddingTop={6}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Upcoming)
